import { useState, useEffect } from "react";
import { loadData, storeData, getCached } from "./s3Data";
import { useIsMobile } from "../useIsMobile";

const CATEGORIES = ["Emergency Contacts", "Medical", "Insurance", "Financial", "Legal", "School", "Home", "Other"];

const CAT_COLORS = {
  "Emergency Contacts": "#E24B4A",
  Medical: "#D4537E",
  Insurance: "#378ADD",
  Financial: "#1D9E75",
  Legal: "#7F77DD",
  School: "#EF9F27",
  Home: "#5A8A6E",
  Other: "#555",
};

const D = {
  root: {
    fontFamily: "'DM Mono', 'Courier New', monospace",
    background: "#0f0f11",
    minHeight: "100vh",
    color: "#e8e6e0",
    paddingTop: 70,
  },
  page: {
    padding: "32px 28px",
    maxWidth: 820,
    margin: "0 auto",
  },
  title: {
    fontFamily: "'Fraunces', Georgia, serif",
    fontSize: 26,
    fontWeight: 600,
    color: "#f0ede6",
    letterSpacing: "-0.02em",
    margin: 0,
    marginBottom: 4,
  },
  sub: {
    fontSize: 10,
    color: "#333",
    letterSpacing: "0.1em",
    textTransform: "uppercase",
    marginBottom: 28,
  },
  catSection: {
    marginBottom: 24,
  },
  catHeader: {
    display: "flex",
    alignItems: "center",
    gap: 8,
    marginBottom: 10,
  },
  catDot: (color) => ({
    width: 8,
    height: 8,
    borderRadius: "50%",
    background: color,
    flexShrink: 0,
  }),
  catLabel: {
    fontSize: 10,
    color: "#555",
    letterSpacing: "0.1em",
    textTransform: "uppercase",
  },
  docCard: {
    background: "#161618",
    border: "1px solid #1e1e22",
    borderRadius: 10,
    padding: "14px 16px",
    marginBottom: 8,
  },
  docLabel: {
    fontSize: 10,
    color: "#444",
    letterSpacing: "0.08em",
    textTransform: "uppercase",
    marginBottom: 4,
  },
  docValue: {
    fontSize: 13,
    color: "#e8e6e0",
    lineHeight: 1.5,
    wordBreak: "break-word",
    whiteSpace: "pre-wrap",
  },
  docActions: {
    display: "flex",
    gap: 8,
    marginTop: 10,
  },
  smallBtn: (variant) => ({
    background: "transparent",
    border: "1px solid " + (variant === "danger" ? "#2a1212" : "#2a2a2e"),
    borderRadius: 6,
    color: variant === "danger" ? "#E24B4A" : "#444",
    fontSize: 10,
    padding: "4px 10px",
    cursor: "pointer",
    fontFamily: "'DM Mono', monospace",
    letterSpacing: "0.04em",
  }),
  addBtn: {
    background: "#1D9E75",
    border: "none",
    borderRadius: 7,
    color: "#fff",
    fontSize: 11,
    padding: "8px 16px",
    cursor: "pointer",
    fontFamily: "'DM Mono', monospace",
    letterSpacing: "0.04em",
  },
  addCard: {
    background: "#111113",
    border: "1px dashed #2a2a2e",
    borderRadius: 12,
    padding: "18px 20px",
    marginBottom: 20,
  },
  label: {
    display: "block",
    fontSize: 9,
    color: "#444",
    letterSpacing: "0.1em",
    textTransform: "uppercase",
    marginBottom: 5,
    marginTop: 10,
  },
  input: {
    width: "100%",
    background: "#111113",
    border: "1px solid #2a2a2e",
    borderRadius: 7,
    padding: "8px 11px",
    color: "#e8e6e0",
    fontSize: 13,
    fontFamily: "'DM Mono', monospace",
    outline: "none",
    boxSizing: "border-box",
  },
  select: {
    width: "100%",
    background: "#111113",
    border: "1px solid #2a2a2e",
    borderRadius: 7,
    padding: "8px 11px",
    color: "#e8e6e0",
    fontSize: 13,
    fontFamily: "'DM Mono', monospace",
    outline: "none",
    cursor: "pointer",
    boxSizing: "border-box",
  },
  textarea: {
    width: "100%",
    background: "#111113",
    border: "1px solid #2a2a2e",
    borderRadius: 7,
    padding: "8px 11px",
    color: "#e8e6e0",
    fontSize: 13,
    fontFamily: "'DM Mono', monospace",
    outline: "none",
    boxSizing: "border-box",
    resize: "vertical",
    minHeight: 80,
  },
};

const BLANK = { label: "", value: "", category: "Emergency Contacts" };

function DocEntry({ doc, onEdit, onDelete }) {
  return (
    <div style={D.docCard}>
      <p style={D.docLabel}>{doc.label}</p>
      <p style={D.docValue}>{doc.value}</p>
      <div style={D.docActions}>
        <button style={D.smallBtn()} onClick={() => onEdit(doc)}>Edit</button>
        <button style={D.smallBtn("danger")} onClick={() => onDelete(doc.id)}>Delete</button>
      </div>
    </div>
  );
}

export default function Docs() {
  const isMobile = useIsMobile();
  const [docs, setDocs] = useState(() => getCached("docs.json", []));
  const [saveStatus, setSaveStatus] = useState(null);
  const [adding, setAdding] = useState(false);
  const [form, setForm] = useState({ ...BLANK });
  const [editingId, setEditingId] = useState(null);

  useEffect(() => {
    loadData("docs.json", null).then(d => { if (d !== null) setDocs(d); }).catch(() => {});
  }, []);

  async function persist(next) {
    setSaveStatus("saving");
    try {
      await storeData("docs.json", next);
      setSaveStatus("saved");
      setTimeout(() => setSaveStatus(null), 1500);
    } catch (e) {
      setSaveStatus("error: " + e.name);
    }
  }

  function save() {
    if (!form.label.trim() || !form.value.trim()) return;
    let next;
    if (editingId) {
      next = docs.map(d => d.id === editingId ? { ...d, ...form } : d);
    } else {
      next = [...docs, { ...form, id: Date.now() }];
    }
    setDocs(next);
    setForm({ ...BLANK });
    setAdding(false);
    setEditingId(null);
    persist(next);
  }

  function startEdit(doc) {
    setForm({ label: doc.label, value: doc.value, category: doc.category });
    setEditingId(doc.id);
    setAdding(true);
  }

  function remove(id) {
    const next = docs.filter(d => d.id !== id);
    setDocs(next);
    persist(next);
  }

  function cancel() {
    setAdding(false);
    setEditingId(null);
    setForm({ ...BLANK });
  }

  const grouped = CATEGORIES.reduce((acc, cat) => {
    const catDocs = docs.filter(d => d.category === cat);
    if (catDocs.length) acc[cat] = catDocs;
    return acc;
  }, {});

  return (
    <div style={D.root}>
      <div style={{ ...D.page, padding: isMobile ? "20px 12px" : D.page.padding }}>
        <div style={{ display: "flex", justifyContent: "space-between", alignItems: "baseline" }}>
          <p style={D.title}>Important Info</p>
          {!adding && (
            <button style={D.addBtn} onClick={() => setAdding(true)}>+ Add Entry</button>
          )}
        </div>
        <p style={D.sub}>
          {saveStatus === "saving" ? "Saving…" : saveStatus === "saved" ? "Saved" : saveStatus === "error" ? "Save failed" : "Family documents & contacts"}
        </p>

        {/* Add / Edit form */}
        {adding && (
          <div style={D.addCard}>
            <label style={{ ...D.label, marginTop: 0 }}>Category</label>
            <select style={D.select} value={form.category} onChange={e => setForm(f => ({ ...f, category: e.target.value }))}>
              {CATEGORIES.map(c => <option key={c}>{c}</option>)}
            </select>

            <label style={D.label}>Label</label>
            <input style={D.input} value={form.label} onChange={e => setForm(f => ({ ...f, label: e.target.value }))} placeholder="e.g. Primary Doctor, Account Number…" />

            <label style={D.label}>Information</label>
            <textarea
              style={D.textarea}
              value={form.value}
              onChange={e => setForm(f => ({ ...f, value: e.target.value }))}
              placeholder="Phone number, address, account details, notes…"
            />

            <div style={{ display: "flex", gap: 8, marginTop: 14 }}>
              <button style={D.addBtn} onClick={save}>{editingId ? "Save Changes" : "Add Entry"}</button>
              <button style={D.smallBtn()} onClick={cancel}>Cancel</button>
            </div>
          </div>
        )}

        {docs.length === 0 && !adding ? (
          <div style={{ textAlign: "center", padding: "48px 0", color: "#222", fontSize: 13 }}>
            No entries yet.<br />
            <span style={{ fontSize: 11 }}>Store emergency contacts, insurance info, account numbers, and more.</span>
          </div>
        ) : (
          Object.entries(grouped).map(([cat, catDocs]) => (
            <div key={cat} style={D.catSection}>
              <div style={D.catHeader}>
                <div style={D.catDot(CAT_COLORS[cat])} />
                <span style={D.catLabel}>{cat}</span>
              </div>
              {catDocs.map(doc => (
                <DocEntry key={doc.id} doc={doc} onEdit={startEdit} onDelete={remove} />
              ))}
            </div>
          ))
        )}
      </div>
    </div>
  );
}
